import type { StudioGenerationAuthorityEnvelope } from "@/features/generation-authority/generation-authority-envelope";
import type { LocaleCode } from "@/pages/cogo-marketing/i18n/locales";

type DesignAxis =
  StudioGenerationAuthorityEnvelope["designAuthority"]["lockedAxes"][number];
type RegisteredSourceKind =
  | "designPack"
  | "designPreset"
  | "designLibrary"
  | "selectedOutcome";

export interface AuthorityContinuityCopy {
  designAxes: Record<DesignAxis, string>;
  sourceFingerprint: (fingerprint: string) => string;
  currentStudioSource: (fingerprint: string) => string;
  registeredSource: string;
  registeredSourceWithIdentity: (
    kind: RegisteredSourceKind,
    identity: string,
    fingerprint: string,
  ) => string;
  registeredSourceFingerprint: (
    kind: RegisteredSourceKind,
    fingerprint: string,
  ) => string;
  generationPaused: string;
  evidenceNeedsReview: string;
  invalidReadbackSummary: string;
  nextStep: string;
  reviewSavedEvidence: string;
  reviewEvidence: string;
  continueBuilding: string;
  continueApplication: (projectName?: string) => string;
  absentReadbackSummary: string;
  continueDescribingApplication: string;
  /**
   * First-run variants of the three lines above. A workspace with no project
   * has nothing to CONTINUE, and saying so was measured on the appliance
   * 2026-08-06 sitting directly above "no work yet". The region still earns
   * its place there — its action focuses the composer — so it keeps the
   * invitation and drops the false claim.
   */
  startBuilding: string;
  describeFirstApplication: string;
  firstRunSummary: string;
  describeApplicationAction: string;
  requiredChecks: string;
  resolveRequiredChecks: string;
  blockingSummary: string;
  requiredChecksToResolve: (count: number) => string;
  reviewRequiredChecks: string;
  nextStepEyebrow: string;
  describeApplication: (projectName?: string) => string;
  creatorEmptySummary: string;
  visualDirectionSelected: string;
  describeHowApplicationWorks: string;
  designOnlySummary: string;
  visualSystem: string;
  selectedVisualSystem: string;
  describeApplicationBehavior: string;
  applicationBehaviorPreserved: string;
  chooseVisualDirection: string;
  appSpecOnlySummary: string;
  applicationFlows: string;
  suppliedApplicationFlows: string;
  suppliedApplicationFlowsCategory: string;
  describeVisualDirection: string;
  accepted: string;
  stillToDecide: string;
  applicationDetails: string;
  clarificationAnswers: string;
  reviewProjectDetails: string;
  confirmWhatShouldBeKept: string;
  noDecisionCategoriesSummary: string;
  reviewProjectRequirements: string;
  requirementsRecorded: string;
  reviewAcceptedRequirements: string;
  acceptedFactsSummary: string;
  reviewAcceptedRequirementsAction: string;
  decisionsStillNeeded: string;
  finishOpenDecisions: string;
  openDecisionsSummary: string;
  continueOpenDecisions: string;
  someDecisionsSet: string;
  finishRemainingDecisions: string;
  mixedDecisionsSummary: string;
  continueRemainingDecisions: string;
  exactRequirementsLocked: string;
  reviewConformanceRequirements: (projectName?: string) => string;
  exactRequirementsSummary: string;
  visualSource: string;
  applicationContract: string;
  suppliedApplicationContract: string;
  lockedRequirements: string;
  lockedRequirementsValue: string;
  reviewConformanceRequirementsAction: string;
  joinCategories: (categories: string[]) => string;
  dataLoadMessage: (label: string) => string;
  retry: string;
  savedWork: string;
  savedProjectDetails: string;
}

const COPY: Record<LocaleCode, AuthorityContinuityCopy> = {
  en: {
    designAxes: {
      style: "visual style",
      baseColor: "base color",
      theme: "theme",
      chart: "chart appearance",
      radius: "corner shape",
      font: "typography",
    },
    sourceFingerprint: (fingerprint) => `Source fingerprint · ${fingerprint}`,
    currentStudioSource: (fingerprint) =>
      `Current Studio source · fingerprint ${fingerprint}`,
    registeredSource: "Registered source for this project",
    registeredSourceWithIdentity: (kind, identity, fingerprint) =>
      `${{ designPack: "Design pack", designPreset: "Design preset", designLibrary: "Design library", selectedOutcome: "Selected outcome" }[kind]} · ${identity} · fingerprint ${fingerprint}`,
    registeredSourceFingerprint: (kind, fingerprint) =>
      `${{ designPack: "Design pack", designPreset: "Design preset", designLibrary: "Design library", selectedOutcome: "Selected outcome" }[kind]} · fingerprint ${fingerprint}`,
    generationPaused: "Generation paused",
    evidenceNeedsReview: "Evidence needs review",
    invalidReadbackSummary:
      "The saved source details could not be verified, so generation cannot continue from them.",
    nextStep: "Next step",
    reviewSavedEvidence: "Review the saved evidence before continuing",
    reviewEvidence: "Review evidence",
    continueBuilding: "Continue building",
    continueApplication: (projectName) =>
      projectName
        ? `Continue your application: ${projectName}`
        : "Continue your application",
    absentReadbackSummary:
      "Nothing has been assumed from a source yet. Describe what you want to build and the conversation continues from there.",
    continueDescribingApplication: "Continue describing the application",
    startBuilding: "Start building",
    describeFirstApplication: "Describe your first application",
    firstRunSummary:
      "Nothing has been assumed from a source yet. Describe what you want to make and cogo takes it from there.",
    describeApplicationAction: "Describe the application",
    requiredChecks: "Required checks",
    resolveRequiredChecks: "Resolve required checks before continuing",
    blockingSummary:
      "Review the listed requirements and resolve them before generation continues.",
    requiredChecksToResolve: (count) => `${count} to resolve`,
    reviewRequiredChecks: "Review required checks",
    nextStepEyebrow: "Next step",
    describeApplication: (projectName) =>
      projectName
        ? `Describe the application for ${projectName}`
        : "Describe the application",
    creatorEmptySummary:
      "Tell Cogo what the application should do, who will use it, and what a successful result looks like.",
    visualDirectionSelected: "Visual direction selected",
    describeHowApplicationWorks: "Now describe how the application works",
    designOnlySummary:
      "The selected visual system will be kept while you describe the application behavior and user journey.",
    visualSystem: "Visual system",
    selectedVisualSystem: "Selected visual system",
    describeApplicationBehavior: "Describe application behavior",
    applicationBehaviorPreserved: "Application behavior preserved",
    chooseVisualDirection: "Choose the visual direction",
    appSpecOnlySummary:
      "The supplied flows remain fixed while you choose how the application should look and feel.",
    applicationFlows: "Application flows",
    suppliedApplicationFlows: "Supplied application flows",
    suppliedApplicationFlowsCategory: "supplied application flows",
    describeVisualDirection: "Describe the visual direction",
    accepted: "Accepted",
    stillToDecide: "Still to decide",
    applicationDetails: "application details",
    clarificationAnswers: "clarification answers",
    reviewProjectDetails: "Review project details",
    confirmWhatShouldBeKept: "Confirm what should be kept",
    noDecisionCategoriesSummary:
      "No decision categories were recorded. Review the saved requirements before continuing.",
    reviewProjectRequirements: "Review project requirements",
    requirementsRecorded: "Requirements recorded",
    reviewAcceptedRequirements: "Review the accepted requirements",
    acceptedFactsSummary:
      "The accepted source facts are recorded. Review them before continuing.",
    reviewAcceptedRequirementsAction: "Review accepted requirements",
    decisionsStillNeeded: "Decisions still needed",
    finishOpenDecisions: "Finish the open decisions",
    openDecisionsSummary:
      "Continue the conversation to decide the parts that remain open.",
    continueOpenDecisions: "Continue the open decisions",
    someDecisionsSet: "Some decisions are set",
    finishRemainingDecisions: "Finish the remaining decisions",
    mixedDecisionsSummary:
      "Accepted source facts will be kept. Continue the conversation to decide the parts that remain open.",
    continueRemainingDecisions: "Continue the remaining decisions",
    exactRequirementsLocked: "Exact requirements are locked",
    reviewConformanceRequirements: (projectName) =>
      projectName
        ? `Review ${projectName} conformance requirements`
        : "Review conformance requirements",
    exactRequirementsSummary:
      "The registered visual source and supplied application contract define what must be checked before generation continues.",
    visualSource: "Visual source",
    applicationContract: "Application contract",
    suppliedApplicationContract: "Supplied application contract",
    lockedRequirements: "Locked requirements",
    lockedRequirementsValue:
      "Visual rules, application flows, and source checks",
    reviewConformanceRequirementsAction: "Review conformance requirements",
    joinCategories: (categories) =>
      `${categories.join(", ").replace(/^./, (value) => value.toUpperCase())}`,
    dataLoadMessage: (label) => `We couldn’t load ${label} right now.`,
    retry: "Try again",
    savedWork: "your saved work",
    savedProjectDetails: "saved project details",
  },
  ko: {
    designAxes: {
      style: "시각 스타일",
      baseColor: "기본 색상",
      theme: "테마",
      chart: "차트 모양",
      radius: "모서리 형태",
      font: "글꼴",
    },
    sourceFingerprint: (fingerprint) => `소스 지문 · ${fingerprint}`,
    currentStudioSource: (fingerprint) =>
      `현재 Studio 소스 · 지문 ${fingerprint}`,
    registeredSource: "이 프로젝트에 등록된 소스",
    registeredSourceWithIdentity: (kind, identity, fingerprint) =>
      `${{ designPack: "디자인 팩", designPreset: "디자인 프리셋", designLibrary: "디자인 라이브러리", selectedOutcome: "선택한 결과물" }[kind]} · ${identity} · 지문 ${fingerprint}`,
    registeredSourceFingerprint: (kind, fingerprint) =>
      `${{ designPack: "디자인 팩", designPreset: "디자인 프리셋", designLibrary: "디자인 라이브러리", selectedOutcome: "선택한 결과물" }[kind]} · 지문 ${fingerprint}`,
    generationPaused: "생성이 일시 중지되었습니다",
    evidenceNeedsReview: "근거를 확인해야 합니다",
    invalidReadbackSummary:
      "저장된 소스 정보를 확인할 수 없어, 이 정보로는 생성을 계속할 수 없습니다.",
    nextStep: "다음 단계",
    reviewSavedEvidence: "계속하기 전에 저장된 근거를 확인하세요",
    reviewEvidence: "근거 확인",
    continueBuilding: "계속 만들기",
    continueApplication: (projectName) =>
      projectName
        ? `애플리케이션 계속하기: ${projectName}`
        : "애플리케이션 계속하기",
    absentReadbackSummary:
      "소스 정보를 추정하지 않았습니다. 만들고 싶은 것을 대화로 설명해 주세요.",
    continueDescribingApplication: "애플리케이션 설명 계속하기",
    startBuilding: "만들기 시작",
    describeFirstApplication: "첫 애플리케이션을 설명해 주세요",
    firstRunSummary:
      "아직 소스 정보를 추정하지 않았습니다. 만들고 싶은 것을 설명해 주시면 cogo가 이어서 진행합니다.",
    describeApplicationAction: "애플리케이션 설명하기",
    requiredChecks: "필수 확인 사항",
    resolveRequiredChecks: "계속하기 전에 필수 확인 사항을 해결하세요",
    blockingSummary:
      "나열된 요구사항을 검토하고 해결한 뒤 생성을 계속할 수 있습니다.",
    requiredChecksToResolve: (count) => `${count}건 해결 필요`,
    reviewRequiredChecks: "필수 확인 사항 검토",
    nextStepEyebrow: "다음 단계",
    describeApplication: (projectName) =>
      projectName
        ? `${projectName} 애플리케이션 설명하기`
        : "애플리케이션 설명하기",
    creatorEmptySummary:
      "애플리케이션이 무엇을 하고, 누가 사용하며, 어떤 결과가 성공인지 Cogo에 알려 주세요.",
    visualDirectionSelected: "시각 방향을 선택했습니다",
    describeHowApplicationWorks: "이제 애플리케이션 작동 방식을 설명하세요",
    designOnlySummary:
      "애플리케이션 동작과 사용자 여정을 설명하는 동안 선택한 시각 시스템을 유지합니다.",
    visualSystem: "시각 시스템",
    selectedVisualSystem: "선택한 시각 시스템",
    describeApplicationBehavior: "애플리케이션 동작 설명",
    applicationBehaviorPreserved: "애플리케이션 동작을 보존했습니다",
    chooseVisualDirection: "시각 방향을 선택하세요",
    appSpecOnlySummary:
      "애플리케이션의 모양과 느낌을 선택하는 동안 제공된 흐름은 유지됩니다.",
    applicationFlows: "애플리케이션 흐름",
    suppliedApplicationFlows: "제공된 애플리케이션 흐름",
    suppliedApplicationFlowsCategory: "제공된 애플리케이션 흐름",
    describeVisualDirection: "시각 방향 설명",
    accepted: "확정됨",
    stillToDecide: "아직 결정할 항목",
    applicationDetails: "애플리케이션 세부 사항",
    clarificationAnswers: "확인 질문 답변",
    reviewProjectDetails: "프로젝트 세부 사항 검토",
    confirmWhatShouldBeKept: "유지할 내용을 확인하세요",
    noDecisionCategoriesSummary:
      "결정 범주가 기록되지 않았습니다. 계속하기 전에 저장된 요구사항을 검토하세요.",
    reviewProjectRequirements: "프로젝트 요구사항 검토",
    requirementsRecorded: "요구사항을 기록했습니다",
    reviewAcceptedRequirements: "확정된 요구사항을 검토하세요",
    acceptedFactsSummary:
      "확정된 소스 사실을 기록했습니다. 계속하기 전에 검토하세요.",
    reviewAcceptedRequirementsAction: "확정된 요구사항 검토",
    decisionsStillNeeded: "결정이 더 필요합니다",
    finishOpenDecisions: "남은 결정을 마치세요",
    openDecisionsSummary: "대화를 계속해 아직 열려 있는 부분을 결정하세요.",
    continueOpenDecisions: "열린 결정 계속하기",
    someDecisionsSet: "일부 결정을 확정했습니다",
    finishRemainingDecisions: "남은 결정을 마치세요",
    mixedDecisionsSummary:
      "확정된 소스 사실은 유지됩니다. 대화를 계속해 아직 열려 있는 부분을 결정하세요.",
    continueRemainingDecisions: "남은 결정 계속하기",
    exactRequirementsLocked: "정확한 요구사항을 고정했습니다",
    reviewConformanceRequirements: (projectName) =>
      projectName
        ? `${projectName} 적합성 요구사항 검토`
        : "적합성 요구사항 검토",
    exactRequirementsSummary:
      "등록된 시각 소스와 제공된 애플리케이션 계약이 생성 전에 확인할 항목을 정합니다.",
    visualSource: "시각 소스",
    applicationContract: "애플리케이션 계약",
    suppliedApplicationContract: "제공된 애플리케이션 계약",
    lockedRequirements: "고정된 요구사항",
    lockedRequirementsValue: "시각 규칙, 애플리케이션 흐름, 소스 확인",
    reviewConformanceRequirementsAction: "적합성 요구사항 검토",
    joinCategories: (categories) => categories.join(", "),
    dataLoadMessage: (label) => `${label}을 지금 불러올 수 없습니다.`,
    retry: "다시 시도",
    savedWork: "저장한 작업",
    savedProjectDetails: "저장된 프로젝트 세부 사항",
  },
  ja: {
    designAxes: {
      style: "ビジュアルスタイル",
      baseColor: "ベースカラー",
      theme: "テーマ",
      chart: "チャートの表示",
      radius: "角の形",
      font: "書体",
    },
    sourceFingerprint: (fingerprint) => `ソース指紋 · ${fingerprint}`,
    currentStudioSource: (fingerprint) =>
      `現在のStudioソース · 指紋 ${fingerprint}`,
    registeredSource: "このプロジェクトに登録されたソース",
    registeredSourceWithIdentity: (kind, identity, fingerprint) =>
      `${{ designPack: "デザインパック", designPreset: "デザインプリセット", designLibrary: "デザインライブラリ", selectedOutcome: "選択した成果物" }[kind]} · ${identity} · 指紋 ${fingerprint}`,
    registeredSourceFingerprint: (kind, fingerprint) =>
      `${{ designPack: "デザインパック", designPreset: "デザインプリセット", designLibrary: "デザインライブラリ", selectedOutcome: "選択した成果物" }[kind]} · 指紋 ${fingerprint}`,
    generationPaused: "生成を一時停止しました",
    evidenceNeedsReview: "根拠の確認が必要です",
    invalidReadbackSummary:
      "保存されたソース詳細を確認できないため、ここから生成を続けられません。",
    nextStep: "次の手順",
    reviewSavedEvidence: "続行する前に保存された根拠を確認してください",
    reviewEvidence: "根拠を確認",
    continueBuilding: "作成を続ける",
    continueApplication: (projectName) =>
      projectName
        ? `アプリケーションを続ける: ${projectName}`
        : "アプリケーションを続ける",
    absentReadbackSummary:
      "ソースに関する主張は仮定していません。作りたいものを会話で説明してください。",
    continueDescribingApplication: "アプリケーションの説明を続ける",
    startBuilding: "作り始める",
    describeFirstApplication: "最初のアプリケーションを説明してください",
    firstRunSummary:
      "まだソースから何も推測していません。作りたいものを説明していただければ、cogo が続きを進めます。",
    describeApplicationAction: "アプリケーションを説明する",
    requiredChecks: "必須確認",
    resolveRequiredChecks: "続行する前に必須確認を解決してください",
    blockingSummary: "記載された要件を確認して解決した後、生成を続けられます。",
    requiredChecksToResolve: (count) => `${count}件の解決が必要`,
    reviewRequiredChecks: "必須確認をレビュー",
    nextStepEyebrow: "次の手順",
    describeApplication: (projectName) =>
      projectName
        ? `${projectName} のアプリケーションを説明`
        : "アプリケーションを説明",
    creatorEmptySummary:
      "アプリケーションの役割、利用者、成功とみなす結果をCogoに伝えてください。",
    visualDirectionSelected: "ビジュアルの方向を選択しました",
    describeHowApplicationWorks: "次にアプリケーションの動きを説明してください",
    designOnlySummary:
      "アプリケーションの動作とユーザーの流れを説明する間、選択したビジュアルシステムを維持します。",
    visualSystem: "ビジュアルシステム",
    selectedVisualSystem: "選択したビジュアルシステム",
    describeApplicationBehavior: "アプリケーションの動作を説明",
    applicationBehaviorPreserved: "アプリケーションの動作を保持しました",
    chooseVisualDirection: "ビジュアルの方向を選択",
    appSpecOnlySummary:
      "アプリケーションの見た目と使い心地を選ぶ間、提供されたフローは固定されたままです。",
    applicationFlows: "アプリケーションフロー",
    suppliedApplicationFlows: "提供されたアプリケーションフロー",
    suppliedApplicationFlowsCategory: "提供されたアプリケーションフロー",
    describeVisualDirection: "ビジュアルの方向を説明",
    accepted: "確定済み",
    stillToDecide: "未決定",
    applicationDetails: "アプリケーションの詳細",
    clarificationAnswers: "確認への回答",
    reviewProjectDetails: "プロジェクトの詳細を確認",
    confirmWhatShouldBeKept: "残す内容を確認",
    noDecisionCategoriesSummary:
      "決定カテゴリが記録されていません。続行する前に保存済み要件を確認してください。",
    reviewProjectRequirements: "プロジェクト要件を確認",
    requirementsRecorded: "要件を記録しました",
    reviewAcceptedRequirements: "確定した要件を確認",
    acceptedFactsSummary:
      "確定したソース事実を記録しました。続行する前に確認してください。",
    reviewAcceptedRequirementsAction: "確定した要件を確認",
    decisionsStillNeeded: "まだ決定が必要です",
    finishOpenDecisions: "未決定事項を完了",
    openDecisionsSummary: "会話を続けて、未決定の部分を決めてください。",
    continueOpenDecisions: "未決定事項を続ける",
    someDecisionsSet: "一部の決定を確定しました",
    finishRemainingDecisions: "残りの決定を完了",
    mixedDecisionsSummary:
      "確定したソース事実は維持されます。会話を続けて、未決定の部分を決めてください。",
    continueRemainingDecisions: "残りの決定を続ける",
    exactRequirementsLocked: "正確な要件を固定しました",
    reviewConformanceRequirements: (projectName) =>
      projectName ? `${projectName} の適合性要件を確認` : "適合性要件を確認",
    exactRequirementsSummary:
      "登録されたビジュアルソースと提供されたアプリケーション契約が、生成前に確認すべき事項を定めます。",
    visualSource: "ビジュアルソース",
    applicationContract: "アプリケーション契約",
    suppliedApplicationContract: "提供されたアプリケーション契約",
    lockedRequirements: "固定された要件",
    lockedRequirementsValue:
      "ビジュアル規則、アプリケーションフロー、ソース確認",
    reviewConformanceRequirementsAction: "適合性要件を確認",
    joinCategories: (categories) => categories.join("、"),
    dataLoadMessage: (label) => `現在、${label}を読み込めません。`,
    retry: "もう一度試す",
    savedWork: "保存した作業",
    savedProjectDetails: "保存したプロジェクトの詳細",
  },
  zh: {
    designAxes: {
      style: "视觉风格",
      baseColor: "基础颜色",
      theme: "主题",
      chart: "图表样式",
      radius: "圆角形状",
      font: "字体",
    },
    sourceFingerprint: (fingerprint) => `来源指纹 · ${fingerprint}`,
    currentStudioSource: (fingerprint) =>
      `当前 Studio 来源 · 指纹 ${fingerprint}`,
    registeredSource: "此项目已登记的来源",
    registeredSourceWithIdentity: (kind, identity, fingerprint) =>
      `${{ designPack: "设计包", designPreset: "设计预设", designLibrary: "设计库", selectedOutcome: "已选结果" }[kind]} · ${identity} · 指纹 ${fingerprint}`,
    registeredSourceFingerprint: (kind, fingerprint) =>
      `${{ designPack: "设计包", designPreset: "设计预设", designLibrary: "设计库", selectedOutcome: "已选结果" }[kind]} · 指纹 ${fingerprint}`,
    generationPaused: "生成已暂停",
    evidenceNeedsReview: "需要检查证据",
    invalidReadbackSummary: "无法验证已保存的来源详情，因此无法据此继续生成。",
    nextStep: "下一步",
    reviewSavedEvidence: "继续前请检查已保存的证据",
    reviewEvidence: "检查证据",
    continueBuilding: "继续创建",
    continueApplication: (projectName) =>
      projectName ? `继续应用：${projectName}` : "继续应用",
    absentReadbackSummary: "尚未从来源假设任何内容。请通过对话描述你想创建的内容。",
    continueDescribingApplication: "继续描述应用",
    startBuilding: "开始创建",
    describeFirstApplication: "描述你的第一个应用",
    firstRunSummary:
      "还没有从来源推断任何内容。描述你想做的东西，cogo 会接着往下走。",
    describeApplicationAction: "描述这个应用",
    requiredChecks: "必需检查",
    resolveRequiredChecks: "继续前请解决必需检查",
    blockingSummary: "请检查并解决列出的要求，然后再继续生成。",
    requiredChecksToResolve: (count) => `需要解决 ${count} 项`,
    reviewRequiredChecks: "检查必需项目",
    nextStepEyebrow: "下一步",
    describeApplication: (projectName) =>
      projectName ? `描述 ${projectName} 应用` : "描述应用",
    creatorEmptySummary:
      "请告诉 Cogo 应用应如何工作、谁会使用以及成功结果是什么。",
    visualDirectionSelected: "已选择视觉方向",
    describeHowApplicationWorks: "现在描述应用如何工作",
    designOnlySummary: "在描述应用行为和用户旅程时，会保留已选的视觉系统。",
    visualSystem: "视觉系统",
    selectedVisualSystem: "已选视觉系统",
    describeApplicationBehavior: "描述应用行为",
    applicationBehaviorPreserved: "已保留应用行为",
    chooseVisualDirection: "选择视觉方向",
    appSpecOnlySummary: "在选择应用外观和体验时，提供的流程将保持固定。",
    applicationFlows: "应用流程",
    suppliedApplicationFlows: "提供的应用流程",
    suppliedApplicationFlowsCategory: "提供的应用流程",
    describeVisualDirection: "描述视觉方向",
    accepted: "已确定",
    stillToDecide: "仍待决定",
    applicationDetails: "应用详情",
    clarificationAnswers: "澄清问题的回答",
    reviewProjectDetails: "检查项目详情",
    confirmWhatShouldBeKept: "确认要保留的内容",
    noDecisionCategoriesSummary: "未记录决定类别。请在继续前检查已保存的要求。",
    reviewProjectRequirements: "检查项目要求",
    requirementsRecorded: "已记录要求",
    reviewAcceptedRequirements: "检查已确定的要求",
    acceptedFactsSummary: "已记录已确定的来源事实。请在继续前检查。",
    reviewAcceptedRequirementsAction: "检查已确定的要求",
    decisionsStillNeeded: "仍需决定",
    finishOpenDecisions: "完成待定决定",
    openDecisionsSummary: "继续对话以决定仍待确定的部分。",
    continueOpenDecisions: "继续待定决定",
    someDecisionsSet: "已确定部分决定",
    finishRemainingDecisions: "完成其余决定",
    mixedDecisionsSummary:
      "将保留已确定的来源事实。继续对话以决定仍待确定的部分。",
    continueRemainingDecisions: "继续其余决定",
    exactRequirementsLocked: "已锁定准确要求",
    reviewConformanceRequirements: (projectName) =>
      projectName ? `查看 ${projectName} 一致性要求` : "查看一致性要求",
    exactRequirementsSummary:
      "已登记的视觉来源和提供的应用合同定义了继续生成前必须检查的内容。",
    visualSource: "视觉来源",
    applicationContract: "应用合同",
    suppliedApplicationContract: "提供的应用合同",
    lockedRequirements: "锁定的要求",
    lockedRequirementsValue: "视觉规则、应用流程和来源检查",
    reviewConformanceRequirementsAction: "查看一致性要求",
    joinCategories: (categories) => categories.join("、"),
    dataLoadMessage: (label) => `目前无法加载${label}。`,
    retry: "重试",
    savedWork: "已保存的工作",
    savedProjectDetails: "已保存的项目详情",
  },
  "zh-TW": {
    designAxes: {
      style: "視覺風格",
      baseColor: "基礎顏色",
      theme: "主題",
      chart: "圖表樣式",
      radius: "圓角形狀",
      font: "字體",
    },
    sourceFingerprint: (fingerprint) => `來源指紋 · ${fingerprint}`,
    currentStudioSource: (fingerprint) =>
      `目前 Studio 來源 · 指紋 ${fingerprint}`,
    registeredSource: "此專案已登記的來源",
    registeredSourceWithIdentity: (kind, identity, fingerprint) =>
      `${{ designPack: "設計包", designPreset: "設計預設", designLibrary: "設計資料庫", selectedOutcome: "已選結果" }[kind]} · ${identity} · 指紋 ${fingerprint}`,
    registeredSourceFingerprint: (kind, fingerprint) =>
      `${{ designPack: "設計包", designPreset: "設計預設", designLibrary: "設計資料庫", selectedOutcome: "已選結果" }[kind]} · 指紋 ${fingerprint}`,
    generationPaused: "生成已暫停",
    evidenceNeedsReview: "需要檢查證據",
    invalidReadbackSummary:
      "無法驗證已儲存的來源詳細資料，因此無法據此繼續生成。",
    nextStep: "下一步",
    reviewSavedEvidence: "繼續前請檢查已儲存的證據",
    reviewEvidence: "檢查證據",
    continueBuilding: "繼續建立",
    continueApplication: (projectName) =>
      projectName ? `繼續應用程式：${projectName}` : "繼續應用程式",
    absentReadbackSummary: "尚未從來源假設任何內容。請透過對話描述你想建立的內容。",
    continueDescribingApplication: "繼續描述應用程式",
    startBuilding: "開始建立",
    describeFirstApplication: "描述你的第一個應用程式",
    firstRunSummary:
      "還沒有從來源推斷任何內容。描述你想做的東西，cogo 會接著往下走。",
    describeApplicationAction: "描述這個應用程式",
    requiredChecks: "必要檢查",
    resolveRequiredChecks: "繼續前請解決必要檢查",
    blockingSummary: "請檢查並解決列出的要求，然後再繼續生成。",
    requiredChecksToResolve: (count) => `需要解決 ${count} 項`,
    reviewRequiredChecks: "檢查必要項目",
    nextStepEyebrow: "下一步",
    describeApplication: (projectName) =>
      projectName ? `描述 ${projectName} 應用程式` : "描述應用程式",
    creatorEmptySummary:
      "請告訴 Cogo 應用程式應如何運作、誰會使用以及成功結果是什麼。",
    visualDirectionSelected: "已選擇視覺方向",
    describeHowApplicationWorks: "現在描述應用程式如何運作",
    designOnlySummary:
      "在描述應用程式行為和使用者旅程時，會保留已選的視覺系統。",
    visualSystem: "視覺系統",
    selectedVisualSystem: "已選視覺系統",
    describeApplicationBehavior: "描述應用程式行為",
    applicationBehaviorPreserved: "已保留應用程式行為",
    chooseVisualDirection: "選擇視覺方向",
    appSpecOnlySummary: "在選擇應用程式外觀和體驗時，提供的流程將保持固定。",
    applicationFlows: "應用程式流程",
    suppliedApplicationFlows: "提供的應用程式流程",
    suppliedApplicationFlowsCategory: "提供的應用程式流程",
    describeVisualDirection: "描述視覺方向",
    accepted: "已確定",
    stillToDecide: "仍待決定",
    applicationDetails: "應用程式詳細資料",
    clarificationAnswers: "澄清問題的回答",
    reviewProjectDetails: "檢查專案詳細資料",
    confirmWhatShouldBeKept: "確認要保留的內容",
    noDecisionCategoriesSummary: "未記錄決定類別。請在繼續前檢查已儲存的要求。",
    reviewProjectRequirements: "檢查專案要求",
    requirementsRecorded: "已記錄要求",
    reviewAcceptedRequirements: "檢查已確定的要求",
    acceptedFactsSummary: "已記錄已確定的來源事實。請在繼續前檢查。",
    reviewAcceptedRequirementsAction: "檢查已確定的要求",
    decisionsStillNeeded: "仍需決定",
    finishOpenDecisions: "完成待定決定",
    openDecisionsSummary: "繼續對話以決定仍待確定的部分。",
    continueOpenDecisions: "繼續待定決定",
    someDecisionsSet: "已確定部分決定",
    finishRemainingDecisions: "完成其餘決定",
    mixedDecisionsSummary:
      "將保留已確定的來源事實。繼續對話以決定仍待確定的部分。",
    continueRemainingDecisions: "繼續其餘決定",
    exactRequirementsLocked: "已鎖定精確要求",
    reviewConformanceRequirements: (projectName) =>
      projectName ? `檢查 ${projectName} 一致性要求` : "檢查一致性要求",
    exactRequirementsSummary:
      "已登記的視覺來源和提供的應用程式合約定義了繼續生成前必須檢查的內容。",
    visualSource: "視覺來源",
    applicationContract: "應用程式合約",
    suppliedApplicationContract: "提供的應用程式合約",
    lockedRequirements: "鎖定的要求",
    lockedRequirementsValue: "視覺規則、應用程式流程和來源檢查",
    reviewConformanceRequirementsAction: "檢查一致性要求",
    joinCategories: (categories) => categories.join("、"),
    dataLoadMessage: (label) => `目前無法載入${label}。`,
    retry: "再試一次",
    savedWork: "已儲存的工作",
    savedProjectDetails: "已儲存的專案詳細資料",
  },
};

export function getAuthorityContinuityCopy(
  locale: LocaleCode,
): AuthorityContinuityCopy {
  return COPY[locale];
}
